import { Col, ConfigProvider, Row, theme } from 'antd';
import { AccountOutlined } from '@vindr/icons';
import { ThemeConfig } from 'antd/es/config-provider/context';
import { StyleProvider, legacyLogicalPropertiesTransformer } from '@ant-design/cssinjs';
import './QMS.css';
import QMSTable from './QMSTable';
import CurrentPatient from './CurrentPatient';
import useDefinedColumnsWaiting from './useDefinedColumnsWaiting';
import useDefinedColumnsMiss from './useDefinedColumnsMiss';
import { CONFIG_SERVER } from '@vindr/services/constants/common.constant';
import { useState, useEffect, useCallback, memo } from 'react';
import { MENU_TYPE, QMS_ITEM_STATUS } from './qms.constant';
import { useUpdateQueueItem, useUpdateQMSItemPosition, useGetQMSDetail } from '@vindr/services/actions/qms.action';
import { findOnlyOneDraggedItem } from '@vindr/utils/helpers';
import { ReactComponent as Logo } from '@vindr/ui/src/assets/logo-light.svg';
import { useTranslation } from 'react-i18next';
import { useGetDetailInstitution } from '@vindr/services/actions/institution.action';
import { ErrorPage } from '@vindr/ui';

const LightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    fontSize: 12,
    borderRadiusSM: 2,
    borderRadiusLG: 2,
    borderRadius: 2,
    colorPrimary: '#4c80a4',
    colorBgContainer: '#fff',
    colorText: '#000',
    colorBgBase: '#f8fafb',
    controlOutlineWidth: 0,
    fontFamily: `Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
  },
  components: {
    Menu: {
      colorItemBgSelected: '#dfeffe',
    },
  },
};

let serverEvents = new Map<string, EventSource>();

const WrapQMSDevice = ({ isPopup = false, qmsId }: { isPopup?: boolean; qmsId: string }) => {
  const { data: qms } = useGetQMSDetail(qmsId);
  const { t } = useTranslation('Vindoc');
  return qms ? <QMSDevice isPopup={isPopup} qmsId={qmsId} qms={qms} /> : <ErrorPage
    title={t('Something went wrong')}
    description={t(
      'Please reload the page or try again after 24 hours. If you continue to have issues, contact the Hotline for assistance.'
    )}
  />;
}

const QMSDevice = ({ isPopup = false, qmsId, qms }: { isPopup?: boolean; qmsId: string; qms: any }) => {
  const { t } = useTranslation('Vindoc');
  const { data: institution } = useGetDetailInstitution(qms?.institution_id, { enabled: !!qms?.institution_id });
  const { mutate: updateQueueItem } = useUpdateQueueItem();
  const { mutate: updateQMSItemPosition } = useUpdateQMSItemPosition();

  const handleContextMenuSelect = useCallback(
    (type: MENU_TYPE, record) => {
      updateQueueItem({ id: record.id, data: { qms_status: QMS_ITEM_STATUS[type] } });
    },
    []
  );
  const waitingColumns = useDefinedColumnsWaiting({ isPopup, config: qms, handleContextMenuSelect });
  const missColumns = useDefinedColumnsMiss({ isPopup, config: qms, handleContextMenuSelect });
  const [queueData, setQueueData] = useState([]);

  if (!serverEvents.has(qmsId)) {
    serverEvents.set(qmsId, new EventSource(CONFIG_SERVER.BACKEND_URL.replace('/api', '') + '/qms/sse?qms_id=' + qmsId));
    serverEvents.get(qmsId).onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (Array.isArray(data)) {
          setQueueData(data);
        } else {
          setQueueData([]);
        }
      } catch (error) {
        console.log(error);
      }
    };
  } else {
    serverEvents.get(qmsId).onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (Array.isArray(data)) {
          setQueueData(data);
        } else {
          setQueueData([]);
        }
      } catch (error) {
        console.log(error);
      }
    };
  }

  useEffect(() => {
    return () => {
      if (serverEvents.has(qmsId)) {
        serverEvents.get(qmsId).close();
        serverEvents.delete(qmsId);
      }
    };
  }, []);

  const missCallData = (Array.isArray(queueData) ? queueData : [])?.filter((it) => it.qms_status === QMS_ITEM_STATUS.MISS_CALL);
  const waitingData = (Array.isArray(queueData) ? queueData : [])?.filter((it) => it.qms_status === QMS_ITEM_STATUS.WAITING);
  const inProgressData = (Array.isArray(queueData) ? queueData : [])?.filter((it) => it.qms_status === QMS_ITEM_STATUS.IN_PROGRESS);

  // const contextMenuItems = useContextMenu({
  //   handleContextMenuSelect,
  //   // selectedRowKeys,
  // });

  const handleDragSortEnd = (type: string) => {
    const beforeData = type === 'waiting' ? waitingData : missCallData;
    return (
      newDataSource: any[],
    ) => {
      console.log(newDataSource);
      const draggedItem = findOnlyOneDraggedItem(beforeData.map(it => it.id), newDataSource.map(it => it.id));
      console.log(draggedItem);
      const tail = draggedItem.to === newDataSource.length - 1 ? null : newDataSource[draggedItem.to + 1].id;
      const head = draggedItem.to === 0 ? null : newDataSource[draggedItem.to - 1].id;
      updateQMSItemPosition({ id: draggedItem.item, data: { tail, head } });
    };
  };

  const handleDoneCurrentPatient = () => {
    updateQueueItem({ id: inProgressData[0].id, data: { qms_status: QMS_ITEM_STATUS.DONE } });
  };

  const handleMissCallCurrentPatient = () => {
    updateQueueItem({ id: inProgressData[0].id, data: { qms_status: QMS_ITEM_STATUS.MISS_CALL } });
  };

  return (
    <div className="qms bg-white">
      <ConfigProvider theme={LightTheme}>
        <StyleProvider transformers={[legacyLogicalPropertiesTransformer]}>
          <header className="flex h-20 items-center justify-between bg-secondary-lighter px-4 py-2">
            <section className="flex w-0 flex-auto items-center gap-4 overflow-hidden">
              <div className="flex justify-center gap-4">
                {institution?.data?.logo ? <img src={institution?.data?.logo} alt="" style={{ height: 56 }} /> : <Logo style={{ height: 56 }} />}
                <div className="flex justify-center items-start text-2xl text-gray-700 font-bold flex-col">
                  <div className="text-2xl font-bold text-gray-700">{qms?.institution?.name}</div>
                  <div className="text-lg text-gray-700">{qms?.name}</div>
                  <div className="text-base text-gray-600">{t('Working Time')}: {qms?.morning_shift_start} - {qms?.morning_shift_end} | {qms?.afternoon_shift_start} - {qms?.afternoon_shift_end}</div>
                </div>
              </div>
            </section>

            <section className="flex items-center">
              {/* <div onClick={() => navigate('/dashboard/overview')} className="mr-3 cursor-pointer">
                  <Icon name="line-chart" className="text-4xl" />
                </div> */}

              {/* <Dropdown
                  menu={{ items: LangItems }}
                  placement="bottomRight"
                  className="header-dropdown-title mr-3 hover:cursor-pointer"
                  overlayClassName="vindr-dropdown select-language header-dropdown w-30"
                >
                  <span className="language-info">
                    <span className="current-lang mr-2 text-base">{t(getCurrentLang(currentLang))}</span>
                    <Icon name="caret-down" className="text-base" />
                  </span>
                </Dropdown> */}

              {/* <ThemeSwitcher /> */}

              <div className="user-info flex items-center hover:cursor-pointer">
                <AccountOutlined className="mr-2 text-[48px]" />
                <div>
                  <div className="user-name text-2xl font-bold text-blue-500 text-uppercase">
                    {qms?.doctor?.fullname || qms?.doctor?.username}
                  </div>
                  {/* <div className="user-name text-base text-green-500">
                    {qms?.doctor?.status ? t('Working') : t('Off')}
                  </div> */}
                </div>
              </div>
            </section>
          </header>
          <div className="p-3">
            <CurrentPatient
              dataSource={inProgressData}
              config={qms}
              isPopup={isPopup}
              onDone={handleDoneCurrentPatient}
              onMissCall={handleMissCallCurrentPatient}
            />
            <Row gutter={[16, 16]}>
              <Col xxl={14} xl={24}>
                <QMSTable
                  columns={waitingColumns}
                  dataSource={waitingData}
                  title={t('QMS.Waiting')}
                  fullHeight={true}
                  onDragSortEnd={handleDragSortEnd('waiting')}
                  // contextMenu={{
                  //   items: contextMenuItems,
                  //   onClick: (menuItem, record: typeof orders[number]) => {
                  //     if (menuItem.onClick) {
                  //       menuItem.onClick(record);
                  //     }
                  //   },
                  // }}
                />
              </Col>
              <Col xxl={10} xl={24}>
                <QMSTable
                  columns={missColumns}
                  dataSource={missCallData}
                  title={t('QMS.Miss Call')}
                  fullHeight={true}
                  onDragSortEnd={handleDragSortEnd('miss')}
                />
              </Col>
            </Row>
          </div>
        </StyleProvider>
      </ConfigProvider>
    </div>
  );
};

export default memo(WrapQMSDevice);
